/**
 * A dropdown that matches the rest of the app.
 *
 * The native control was being used raw, so a season picker rendered as the
 * operating system's grey chrome in the middle of a page that is otherwise flat
 * ink on paper, and it looked like a form someone forgot to style.
 *
 * It is still a native `select` underneath. A hand-built listbox would need
 * keyboard handling, focus trapping and scroll containment to match what the
 * browser already does, and on a phone it would lose the native wheel, which is
 * the better control on a small screen. So the element is made transparent and
 * laid over a styled shell, which keeps every behaviour and changes only the skin.
 */
export function Select<T extends string | number>({
  value,
  onChange,
  options,
  label,
  tone = 'ink',
  className = '',
}: {
  value: T;
  onChange: (v: string) => void;
  options: readonly (readonly [T, string])[];
  label: string;
  /** `paper` for a dark panel, `ink` for the page. */
  tone?: 'ink' | 'paper';
  className?: string;
}) {
  const dark = tone === 'paper';
  return (
    <span
      className={`relative inline-flex h-9 items-center rounded-sm border transition-colors
        ${dark
          ? 'border-white/40 bg-white/15 text-paper hover:border-white/70'
          : 'border-ink/25 bg-paper text-ink hover:border-blue'}
        focus-within:border-blue focus-within:ring-2 focus-within:ring-blue/30 ${className}`}
    >
      <select
        aria-label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="peer h-full w-full cursor-pointer appearance-none bg-transparent py-0 pl-3 pr-8
                   font-cond text-[15px] font-semibold tracking-[0.02em] outline-none"
      >
        {options.map(([v, text]) => (
          <option key={v} value={v} className="bg-paper font-sans text-ink">
            {text}
          </option>
        ))}
      </select>
      {/* the native arrow is part of the chrome being replaced */}
      <svg
        viewBox="0 0 10 6"
        aria-hidden="true"
        className={`pointer-events-none absolute right-2.5 h-[6px] w-[10px] ${dark ? 'text-paper/70' : 'text-muted'}`}
      >
        <path d="M0 0h10L5 6z" fill="currentColor" />
      </svg>
    </span>
  );
}
